import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clock, AlertCircle, CheckCircle, XCircle, Eye, Phone, Mail, Filter, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const AdminPaymentVerification = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({ total_payments: 0, pending_verifications: 0, verified_payments: 0, rejected_payments: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchPendingPayments();
    fetchStatistics();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchPendingPayments();
      fetchStatistics();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchPendingPayments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/payments/pending-verifications`
      );
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/payments/statistics`
      );
      setStats(response.data.statistics || {});
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const submitted = new Date(date);
    const diffMs = now - submitted;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const getTimeUntilDeadline = (deadline) => {
    const now = new Date();
    const end = new Date(deadline);
    const diffMs = end - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffMs <= 0) return 'Expired';
    if (diffHours < 1) return `${diffMins} minutes`;
    return `${diffHours}h ${diffMins}m`;
  };

  const isUrgent = (deadline) => {
    const now = new Date();
    const end = new Date(deadline);
    const diffHours = (end - now) / (1000 * 60 * 60);
    return diffHours <= 1;
  };

  const filteredPayments = payments.filter(payment => {
    if (filter === 'urgent') return isUrgent(payment.payment_deadline);
    if (filter === 'new') {
      const hoursSinceSubmit = (new Date() - new Date(payment.updated_at)) / (1000 * 60 * 60);
      return hoursSinceSubmit < 1;
    }
    return true;
  });

  const sortedPayments = [...filteredPayments].sort((a, b) => {
    if (sortBy === 'newest') return new Date(b.updated_at) - new Date(a.updated_at);
    if (sortBy === 'oldest') return new Date(a.updated_at) - new Date(b.updated_at);
    if (sortBy === 'amount_high') return b.amount - a.amount;
    if (sortBy === 'amount_low') return a.amount - b.amount;
    return 0;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/dashboard')}
                className="text-white hover:bg-white/20 mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-3xl font-bold">Payment Verification Queue</h1>
              <p className="text-white/80">Review and approve pending bank transfer payments</p>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm">Pending</p>
                    <p className="text-3xl font-bold text-white">{stats.pending_verifications}</p>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-300" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm">Today</p>
                    <p className="text-3xl font-bold text-white">{stats.total_payments}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-blue-300" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm">Approved</p>
                    <p className="text-3xl font-bold text-white">{stats.verified_payments}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-300" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm">Rejected</p>
                    <p className="text-3xl font-bold text-white">{stats.rejected_payments}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-semibold">FILTERS:</span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={filter === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilter('all')}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={filter === 'urgent' ? 'default' : 'outline'}
                  onClick={() => setFilter('urgent')}
                  className={filter === 'urgent' ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  🔴 Urgent (&lt;1hr)
                </Button>
                <Button
                  size="sm"
                  variant={filter === 'new' ? 'default' : 'outline'}
                  onClick={() => setFilter('new')}
                >
                  New
                </Button>
              </div>

              <div className="ml-auto flex items-center gap-2">
                <span className="text-sm font-semibold">SORT BY:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="amount_high">Amount (High)</SelectItem>
                    <SelectItem value="amount_low">Amount (Low)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment List */}
        {sortedPayments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">All Caught Up!</h3>
              <p className="text-gray-600">No pending payment verifications at the moment.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sortedPayments.map((payment) => (
              <Card
                key={payment.order_id}
                className={`transition-all hover:shadow-lg ${
                  isUrgent(payment.payment_deadline) ? 'border-red-400 border-2' : ''
                }`}
              >
                <CardContent className="p-6">
                  {/* Urgent Badge */}
                  {isUrgent(payment.payment_deadline) && (
                    <div className="mb-3">
                      <Badge className="bg-red-600 text-white">
                        🔴 URGENT - Expires in {getTimeUntilDeadline(payment.payment_deadline)}
                      </Badge>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Order ID</p>
                        <p className="text-lg font-bold text-blue-600">{payment.order_id}</p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Customer</p>
                        <p className="font-semibold text-gray-900">{payment.user?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{payment.user?.email}</p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Service</p>
                        <p className="font-semibold text-gray-900">
                          {payment.service_type?.replace(/_/g, ' ')} - {payment.service_duration} Days
                        </p>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Amount</p>
                        <p className="text-2xl font-bold text-gray-900">Rs. {payment.amount?.toLocaleString()}</p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Bank</p>
                        <p className="font-semibold text-gray-900">{payment.selected_bank}</p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Submitted</p>
                        <p className="font-semibold text-gray-900">
                          {getTimeAgo(payment.updated_at)} ({new Date(payment.updated_at).toLocaleString()})
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex gap-3 pt-4 border-t">
                    <Button
                      onClick={() => navigate(`/admin/payment/review/${payment.order_id}`)}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Review Now
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.location.href = `mailto:${payment.user?.email}`}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => alert('Phone call feature coming soon')}
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Call
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Keyboard Shortcuts Help */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-900">
              <strong>⌨️ Keyboard Shortcuts:</strong> A - Approve | R - Reject | N - Next | P - Previous | 
              C - Contact | F - Flag | ESC - Back to queue
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPaymentVerification;
